import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { Lock, CreditCard, Minus, Plus, Loader2, FileText, CheckCircle2, Clock, Hand, ArrowRight, Languages } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { setSiteLang, useSiteLang } from "@/components/PublicShell";
import OrgNameInput from "@/components/OrgNameInput";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api, errMsg, money } from "@/lib/api";
import { OrderCardAuth, CardAuth, cardAuthError } from "@/components/OrderCardAuth";

type Form = { org_name: string; contact_name: string; contact_email: string; phone: string; address: string; quantity: number; notes: string; payment_method: "card" | "invoice" };

const T = {
  en: {
    toggle: "Español", eyebrow: "For organizations", title: "Order background screening codes",
    lead: "Tell us about your organization and how many employees and volunteers you'd like screened. Your codes appear on screen the moment your payment goes through, and we also email them to you as a list and a PDF.",
    details: "Organization details", orgName: "Organization name", yourName: "Your name", phone: "Phone number", email: "Email address", emailHint: "Your codes will be sent to this address.",
    addrInv: "Mailing address (required for invoice)", addrOpt: "Mailing address (optional)", addrHint: "Your invoice will be mailed here.", notes: "Notes for our team (optional)",
    yourOrder: "Your order", qty: "Number of screening codes", price: "Price per code", subtotal: "Subtotal", tax: "Any applicable tax is calculated at checkout.",
    how: "How would you like to pay?", card: "Pay by card (recommended)", cardText: "Fastest. Your codes appear instantly after payment.", inv: "Invoice me", invText: "We review your order first, then email your codes.",
    invNote: "You will be invoiced by mail. This method takes longer: our team must approve the order before your codes are issued. For the fastest service, we encourage you to pay by credit card.",
    confirmA: "I am ordering for an organization", confirmB: ", not for myself as an employee, volunteer or applicant.",
    confirmErr: "Please confirm you are ordering for an organization. Applicants get their code from their organization.",
    submitInv: "Submit invoice order", submitCard: "Continue to secure payment", stripe: "Card details are entered on Stripe's secure checkout page and never touch our servers.",
    filled: "Your organization details are filled in",
    doneTitle: "Invoice order received",
    done: (o: any) => `Order ${o.number} for ${o.quantity} code${o.quantity === 1 ? "" : "s"} (${money(o.amount)} plus any tax). Our team will review it, then email your codes to ${o.contact_email} and mail your invoice. This usually takes longer than paying by card.`,
    needToday: "Need codes today?", payCard: "Order and pay by card", instantly: "to get them instantly.", home: "Back to home",
  },
  es: {
    toggle: "English", eyebrow: "Para organizaciones", title: "Comprar códigos de verificación para voluntarios",
    lead: "Cuéntenos sobre su organización y cuántos voluntarios desea verificar. Sus códigos aparecen en pantalla en cuanto se procesa el pago y también se los enviamos por correo electrónico como lista y en PDF.",
    details: "Datos de la organización", orgName: "Nombre de la organización", yourName: "Su nombre", phone: "Número de teléfono", email: "Correo electrónico", emailHint: "Le enviaremos los códigos a esta dirección.",
    addrInv: "Dirección postal (obligatoria para factura)", addrOpt: "Dirección postal (opcional)", addrHint: "Le enviaremos la factura por correo a esta dirección.", notes: "Notas para nuestro equipo (opcional)",
    yourOrder: "Su pedido", qty: "Cantidad de códigos para voluntarios", price: "Precio por código", subtotal: "Subtotal", tax: "Los impuestos aplicables se calculan al pagar.",
    how: "¿Cómo desea pagar?", card: "Pagar con tarjeta (recomendado)", cardText: "Lo más rápido. Sus códigos aparecen al instante después del pago.", inv: "Envíenme una factura", invText: "Primero revisamos su pedido y luego le enviamos los códigos por correo.",
    invNote: "Recibirá la factura por correo postal. Este método tarda más: nuestro equipo debe aprobar el pedido antes de emitir los códigos. Para un servicio más rápido, le recomendamos pagar con tarjeta de crédito.",
    confirmA: "Compro para una organización", confirmB: ", no para mí como voluntario o solicitante.",
    confirmErr: "Confirme que compra para una organización. Los solicitantes reciben su código de su organización.",
    submitInv: "Enviar pedido con factura", submitCard: "Continuar al pago seguro", stripe: "Los datos de la tarjeta se ingresan en la página de pago segura de Stripe y nunca pasan por nuestros servidores.",
    filled: "Los datos de su organización ya están completados",
    doneTitle: "Pedido con factura recibido",
    done: (o: any) => `Pedido ${o.number} de ${o.quantity} código${o.quantity === 1 ? "" : "s"} (${money(o.amount)} más impuestos). Nuestro equipo lo revisará, luego enviará sus códigos a ${o.contact_email} y le enviará la factura por correo. Esto suele tardar más que pagar con tarjeta.`,
    needToday: "¿Necesita los códigos hoy?", payCard: "Compre y pague con tarjeta", instantly: "para recibirlos al instante.", home: "Volver al inicio",
  },
};
type Tx = typeof T.en;

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">{label}</label>
      {children}
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

function PayOption({ value, current, onPick, icon, title, text }: { value: string; current: string; onPick: () => void; icon: React.ReactNode; title: string; text: string }) {
  const on = value === current;
  return (
    <button type="button" onClick={onPick} data-testid={`org-order-pay-${value}`}
      className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-[#1B365D] bg-[#F1F5FB]" : "border-slate-200 hover:border-slate-300"}`}>
      <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${on ? "border-[#1B365D]" : "border-slate-300"}`}>{on && <span className="h-2.5 w-2.5 rounded-full bg-[#1B365D]" />}</span>
      <span><span className="flex items-center gap-1.5 text-sm font-bold text-[#1A2536]">{icon} {title}</span><span className="mt-0.5 block text-xs text-[#64748B]">{text}</span></span>
    </button>
  );
}

const WARN = {
  en: { title: "Are you an employee, volunteer or applicant? Please stop here.", body: <>Codes are <b>only sold to organizations</b> (churches, schools and nonprofits). <b>Do not buy a code for yourself.</b> Your organization will give you your code. If you don't have one yet, please ask the person who asked you to apply.</>, apply: "I already have a code", status: "Check my application status", to: "/check-status" },
  es: { title: "¿Es usted voluntario o solicitante? Por favor, deténgase aquí.", body: <>Los códigos <b>solo se venden a organizaciones</b> (iglesias, escuelas y organizaciones sin fines de lucro). <b>No compre un código para usted.</b> Su organización le dará su código. Si todavía no lo tiene, pídaselo a la persona que le invitó a ser voluntario.</>, apply: "Ya tengo un código", status: "Consultar el estado de mi solicitud", to: "/check-status?lang=es" },
};

function WarnBlock({ l, sub }: { l: "en" | "es"; sub?: boolean }) {
  const w = WARN[l];
  const s = sub ? "-alt" : "";
  return (
    <div lang={l} className={sub ? "mt-6 border-t border-dashed border-[#F59E0B]/60 pt-5" : ""} data-testid={`order-applicant-warning-${l}`}>
      {sub && <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#B45309]">{l === "es" ? "En español" : "In English"}</p>}
      <p className={`font-heading font-extrabold tracking-tight text-[#7C2D12] ${sub ? "mt-1.5 text-lg sm:text-xl" : "text-xl sm:text-2xl"}`}>{w.title}</p>
      <p className="mt-2 text-base leading-relaxed text-[#7C4A03]">{w.body}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Link to="/apply" className="inline-flex items-center gap-2 rounded-xl bg-[#1B365D] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#142847]" data-testid={`order-warning-apply-link${s}`}>{w.apply} <ArrowRight className="h-4 w-4" /></Link>
        <Link to={w.to} className="inline-flex items-center gap-2 rounded-xl border border-[#D97706] bg-white px-4 py-2.5 text-sm font-semibold text-[#7C2D12] transition-colors hover:bg-[#FEF3C7]" data-testid={`order-warning-status-link${s}`}>{w.status}</Link>
      </div>
    </div>
  );
}

function ApplicantWarning({ lang }: { lang: "en" | "es" }) {
  return (
    <div className="fade-up mt-8 overflow-hidden rounded-3xl border-2 border-[#F59E0B] bg-[#FFFBEB] shadow-[0_18px_40px_-24px_rgba(180,83,9,0.55)]" role="alert" data-testid="order-applicant-warning">
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-start sm:p-7">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#F59E0B] text-white"><Hand className="h-7 w-7" /></span>
        <div className="flex-1">
          <WarnBlock l={lang} />
          <WarnBlock l={lang === "es" ? "en" : "es"} sub />
        </div>
      </div>
    </div>
  );
}

function InvoiceDone({ o, t }: { o: any; t: Tx }) {
  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-4 pt-16">
        <div className="card-soft fade-up p-8 sm:p-10" data-testid="invoice-order-confirmation">
          <div className="grid h-14 w-14 place-items-center rounded-3xl bg-[#FFF1CC]"><Clock className="h-7 w-7 text-[#B45309]" /></div>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight">{t.doneTitle}</h1>
          <p className="mt-3 text-[#475569]" data-testid="invoice-order-number">{t.done(o)}</p>
          <p className="mt-4 text-sm text-[#475569]">{t.needToday} <Link to="/order" reloadDocument className="font-semibold text-[#1B365D] underline" data-testid="invoice-order-pay-card-link">{t.payCard}</Link> {t.instantly}</p>
          <Link to="/" className="btn-secondary mt-8" data-testid="invoice-order-home-link">{t.home}</Link>
        </div>
      </div>
    </PublicShell>
  );
}

export default function OrderPage() {
  const lang = useSiteLang();
  const t = T[lang];
  const [price, setPrice] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<any>(null);
  const [isOrg, setIsOrg] = useState(false);
  const [auth, setAuth] = useState<CardAuth>({ agreed: false, name: "", image: null });
  const reorder = (useLocation().state as any)?.reorder || {};
  const [f, setF] = useState<Form>({ org_name: "", contact_name: "", contact_email: "", phone: "", address: "", quantity: 5, notes: "", payment_method: "card", ...reorder });
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const r = useSearchParams()[0].get("r");
  useEffect(() => {
    if (!r) return;
    api.get(`/public/reorder/${r}`).then(({ data }) => {
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v)) }));
      toast.success(t.filled);
    }).catch((e) => toast.error(errMsg(e)));
  }, [r]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/public/config").then((r) => setPrice(r.data.price_per_code)).catch(() => null);
  }, []);

  const qty = Math.max(1, Math.min(500, Number(f.quantity) || 1));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOrg) { toast.error(t.confirmErr); return; }
    const authErr = f.payment_method === "card" ? cardAuthError(auth, lang === "es") : null;
    if (authErr) { toast.error(authErr); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/orders", { ...f, quantity: qty, lang, origin_url: window.location.origin, ...(f.payment_method === "card" ? { auth_agreed: auth.agreed, auth_signature_name: auth.name.trim(), auth_signature_image: auth.image } : {}) });
      if (data.invoice) { setDone(data); window.scrollTo(0, 0); return; }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const invoice = f.payment_method === "invoice";
  if (done) return <InvoiceDone o={done} t={t} />;
  return (
    <PublicShell>
      <section className="mx-auto max-w-6xl px-4 pt-12 sm:px-6 lg:px-8" lang={lang}>
        <div className="fade-up max-w-2xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#D97706]">{t.eyebrow}</p>
            <button type="button" onClick={() => setSiteLang(lang === "es" ? "en" : "es")} data-testid="order-lang-toggle"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#1B365D] bg-white px-4 py-1.5 text-sm font-bold text-[#1B365D] transition-colors hover:bg-[#1B365D] hover:text-white">
              <Languages className="h-4 w-4" /> {t.toggle}
            </button>
          </div>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="order-title">{t.title}</h1>
          <p className="mt-4 text-lg text-[#475569]">{t.lead}</p>
        </div>
        <ApplicantWarning lang={lang} />
        <form onSubmit={submit} className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="order-form">
          <div className="card-soft space-y-6 p-6 sm:p-8">
            <h2 className="text-xl font-bold">{t.details}</h2>
            <Field id="org_name" label={t.orgName}>
              <OrgNameInput es={lang === "es"} value={f.org_name} onChange={(v) => setF((p) => ({ ...p, org_name: v }))} />
            </Field>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field id="contact_name" label={t.yourName}>
                <input id="contact_name" data-testid="org-order-contact-input" required minLength={2} className="field-input" value={f.contact_name} onChange={set("contact_name")} />
              </Field>
              <Field id="phone" label={t.phone}>
                <input id="phone" data-testid="org-order-phone-input" required minLength={7} type="tel" className="field-input" value={f.phone} onChange={set("phone")} placeholder="(302) 555-0100" />
              </Field>
            </div>
            <Field id="contact_email" label={t.email} hint={t.emailHint}>
              <input id="contact_email" data-testid="org-order-email-input" required type="email" className="field-input" value={f.contact_email} onChange={set("contact_email")} />
            </Field>
            <Field id="address" label={invoice ? t.addrInv : t.addrOpt} hint={invoice ? t.addrHint : undefined}>
              <AddressAutocomplete id="address" testId="org-order-address-input" required={invoice} minLength={invoice ? 8 : undefined} value={f.address} onChange={(v) => setF((p) => ({ ...p, address: v }))} onPick={(s) => setF((p) => ({ ...p, address: s.label }))} />
            </Field>
            <Field id="notes" label={t.notes}>
              <textarea id="notes" data-testid="org-order-notes-input" rows={3} className="field-input h-auto py-3" value={f.notes} onChange={set("notes")} />
            </Field>
          </div>
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="card-soft p-6 sm:p-8">
              <h2 className="text-xl font-bold">{t.yourOrder}</h2>
              <label className="field-label mt-6" htmlFor="qty">{t.qty}</label>
              <div className="flex items-center gap-3">
                <button type="button" data-testid="org-order-qty-minus" className="btn-secondary h-12 w-12 shrink-0 px-0" onClick={() => setF({ ...f, quantity: Math.max(1, qty - 1) })}><Minus className="h-5 w-5 shrink-0" /></button>
                <input id="qty" data-testid="org-order-code-quantity-input" type="number" min={1} max={500} className="field-input text-center font-mono text-lg" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} />
                <button type="button" data-testid="org-order-qty-plus" className="btn-secondary h-12 w-12 shrink-0 px-0" onClick={() => setF({ ...f, quantity: Math.min(500, qty + 1) })}><Plus className="h-5 w-5 shrink-0" /></button>
              </div>
              <dl className="mt-6 space-y-3 border-t border-dashed border-slate-200 pt-5 text-[15px]">
                <div className="flex justify-between text-[#64748B]"><dt>{t.price}</dt><dd>{price != null ? money(price) : "—"}</dd></div>
                <div className="flex justify-between text-lg font-bold"><dt>{t.subtotal}</dt><dd data-testid="org-order-total">{price != null ? money(price * qty) : "—"}</dd></div>
                <p className="text-xs text-[#94A3B8]">{t.tax}</p>
              </dl>
              <div className="mt-6 space-y-2" data-testid="org-order-payment-method">
                <p className="field-label">{t.how}</p>
                <PayOption value="card" current={f.payment_method} onPick={() => setF({ ...f, payment_method: "card" })} icon={<CreditCard className="h-4 w-4" />} title={t.card} text={t.cardText} />
                <PayOption value="invoice" current={f.payment_method} onPick={() => setF({ ...f, payment_method: "invoice" })} icon={<FileText className="h-4 w-4" />} title={t.inv} text={t.invText} />
              </div>
              {invoice && (
                <p className="mt-3 rounded-xl bg-[#FFF1CC] px-3 py-2.5 text-xs leading-relaxed text-[#7C4A03]" data-testid="org-order-invoice-note">{t.invNote}</p>
              )}
              {!invoice && <OrderCardAuth a={auth} setA={setAuth} amount={(price || 0) * qty} qty={qty} lang={lang === "es" ? "es" : "en"} />}
              <label className={`mt-6 flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-colors ${isOrg ? "border-[#1B365D] bg-[#F1F5FB]" : "border-[#F59E0B] bg-[#FFFBEB]"}`} data-testid="org-order-confirm-org">
                <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[#1B365D]" checked={isOrg} onChange={(e) => setIsOrg(e.target.checked)} data-testid="org-order-confirm-org-checkbox" />
                <span className="text-sm leading-snug text-[#1A2536]"><b>{t.confirmA}</b>{t.confirmB}</span>
              </label>
              <button type="submit" disabled={busy || !isOrg || (!invoice && !price)} data-testid="org-order-checkout-button" className="btn-primary mt-6 w-full">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : invoice ? <CheckCircle2 className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />} {invoice ? t.submitInv : t.submitCard}
              </button>
              <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-[#64748B]">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t.stripe}
              </p>
            </div>
          </aside>
        </form>
      </section>
    </PublicShell>
  );
}
